<template>
	<div>
		<div class="cart-grid">
			<div
				:class="['cart-items', { 'cart-items--conflict': hasConflict(item) }]"
				v-for="(item, index) in cart"
				:key="'cart' + index"
			>
				<div
					class="cart-info"
					@touchstart="touchStart"
					@touchend="touchend($event, item, index)"
				>
					<div
						:class="[
							'cart-btn',
							{ 'cart-btn--disabled': !canEditItem(item) || isRepicking },
						]"
						:aria-disabled="!canEditItem(item) || isRepicking"
						@click="handleEditClick(item, index)"
					>
						<img src="../assets/pen.svg" alt="" />
					</div>
					<div class="cart-name">{{ item.itemName }}</div>
					<ByAmountPicker
						v-model="item.count"
						@onIncrease="cartItem('plus', item, index, $event)"
						@onMinus="cartItem('minus', item, index, $event)"
						@onDelete="deleteItem(item, index)"
						:disable="item.freeItem || !canEditItem(item)"
						:isUpdate="false"
					/>
					<div class="cart-price">
						${{ $stepPrice($countProductPrice(item)) }}
					</div>
					<OrderList class="cart-order-list" :data="item" />
					<div v-if="hasConflict(item)" class="cart-conflict" role="alert">
						<p
							v-for="message in conflictMessages(item)"
							:key="message"
							class="cart-conflict-reason"
						>
							<span class="cart-conflict-icon" aria-hidden="true">⚠</span>
							{{ message }}
						</p>
						<!-- 刪除不放在這裡：左滑既有刪除鍵已可移除該筆 -->
						<div
							v-if="canAccept(item) || canRepick(item)"
							class="cart-conflict-actions"
						>
							<button
								v-if="canAccept(item)"
								class="cart-conflict-btn cart-conflict-btn--accept"
								@click="acceptNewPrice(item, index)"
							>
								接受新價
							</button>
							<button
								v-if="canRepick(item)"
								class="cart-conflict-btn cart-conflict-btn--repick"
								:disabled="isRepicking"
								@click="repickConflictItem(item, index)"
							>
								重新選購
							</button>
						</div>
					</div>
					<div v-if="item.automaticDiscountId" class="cart-discount">
						<p>折扣：</p>
						<div class="cart-discount-content">
							<span> 「{{ item.automaticDiscountName }}」 </span>
							<span>-${{ Math.trunc(item.discountPrice * item.count) }}</span>
						</div>
					</div>
				</div>
				<div
					class="cart-delete"
					@click="onShowDeleteCartItemDialog(item, index)"
				>
					<div class="trashcan">
						<img src="../assets/trashcan.svg" alt="" />
					</div>
				</div>
			</div>
		</div>

		<!--	手機點餐下顯示訂單自動折扣	-->
		<template v-if="!isKiosk">
			<div class="cart-order-discount">
				<div class="cart-order-discount-use" v-if="useOrderAutomaticDiscount">
					<div class="cart-order-discount-use-text">
						「{{ useOrderAutomaticDiscount.name }}」
						<span
							v-if="
								useOrderAutomaticDiscount &&
								useOrderAutomaticDiscount.discountType === 'FREE_ITEM' &&
								!cartHasFreeItem
							"
							@click="goFreeItemDetail()"
						>
							<img
								class="go-free-item-detail-button"
								src="../assets/plus.svg"
								alt="go-free-item-detail-button"
							/>
						</span>
					</div>
					<span v-if="orderAutomaticDiscountPrice"
						>-${{ orderAutomaticDiscountPrice }}</span
					>
				</div>
				<div class="cart-order-discount-next" v-if="nextOrderAutomaticDiscount">
					*差${{
						nextOrderAutomaticDiscount.minimumSpendingAmount - cartTotal
					}}元 即可達到 「{{ nextOrderAutomaticDiscount.name }}」
				</div>
			</div>
		</template>

		<div class="cart-count-capped" v-if="!noCount && isDiscountCapped">
			折抵已達上限，應付 $0
		</div>
		<div class="cart-count" v-if="!noCount">
			<span>總計</span>
			<span>$ {{ $stepPrice(payableTotal) }}</span>
		</div>
		<ByDialog
			class="clear-cart-confirm"
			:value="isDeleteCartItemDialog"
			width="320px"
			:title="`是否刪除${cacheDeleteCartItemState.name}`"
			@closeDialog="onHideDeleteCartItemDialog"
		>
			<template slot="dialog-footer">
				<div class="dialog-footer">
					<ByButton stl="main2" @click="onHideDeleteCartItemDialog"
						>否
					</ByButton>
					<ByButton stl="main1" @click="deleteCartItem">是</ByButton>
				</div>
			</template>
		</ByDialog>
	</div>
</template>
<script>
import { mapMutations } from 'vuex'
import {
	CART_CONFLICT_TYPES,
	formatConflictMessage,
	canAcceptNewPrice,
} from '@/utils/compareCart'
import { captureFullMenuRequest } from '@/utils/menuCache'
import {
	getOrderPayableTotal,
	isDiscountCappedAtZero,
	resolveItemAutomaticDiscount,
} from '@/utils/order'
import OrderList from '@/views/buy/Components/OrderList'
import ByAmountPicker from '@/components/AmountPicker.vue'
import ByDialog from '@/components/Dialog.vue'
import ByButton from '@/components/Button.vue'

export default {
	name: 'ByCartGrid',
	components: { ByButton, ByDialog, ByAmountPicker, OrderList },
	props: {
		noCount: {
			type: Boolean,
			required: false,
			default: false,
		},
		// 取代原本的 handleEditProduct
		editFun: {
			type: Function,
			required: false,
			default: null,
		},
	},
	data() {
		return {
			startX: 0, //開始觸摸的位置
			endX: 0, //結束觸摸的位置
			isDeleteCartItemDialog: false,
			isRepicking: false,
			cacheDeleteCartItemState: {
				index: 0,
				name: '',
			},
		}
	},
	computed: {
		qrCodeHash() {
			return this.$storeManager.qrCodeHash
		},
		storeId() {
			return this.$storeManager.storeId
		},
		itemAutomaticDiscountList() {
			return this.$store.state.store.itemAutomaticDiscountList
		},
		automaticDiscountListReady() {
			return this.$store.state.store.automaticDiscountListReady
		},
		cart() {
			const cart = this.$store.getters['user/currCart'](this.qrCodeHash)
			return cart || []
		},
		cartTotal() {
			return this.$store.getters['user/cartTotal'](this.qrCodeHash) || 0
		},
		// 訂單自動折扣金額(折數換算金額、金額)
		orderAutomaticDiscountPrice() {
			const storeId = this.storeId
			return storeId
				? this.$store.getters['user/orderAutomaticDiscountPriceByStore'](
						storeId,
					)
				: 0
		},
		payableTotal() {
			return getOrderPayableTotal(
				this.cartTotal,
				this.orderAutomaticDiscountPrice,
			)
		},
		isDiscountCapped() {
			return isDiscountCappedAtZero(
				this.cartTotal,
				this.orderAutomaticDiscountPrice,
			)
		},
		// 套用的訂單自動折扣
		useOrderAutomaticDiscount() {
			const storeId = this.storeId
			return storeId
				? this.$store.getters['user/useOrderAutomaticDiscountByStore'](storeId)
				: null
		},
		// 目標訂單自動折扣
		nextOrderAutomaticDiscount() {
			const storeId = this.storeId
			return storeId
				? this.$store.getters['user/nextOrderAutomaticDiscountByStore'](storeId)
				: null
		},
		// 是否前台點餐
		isKiosk() {
			return this.$store.getters.isKiosk
		},
		menus() {
			return this.$store.state.store.menus
		},
		isFullMenuFreshForCartConflicts() {
			return this.$store.getters['store/isFullMenuFreshForCartConflicts'](
				this.storeId,
			)
		},
		canBuy() {
			return this.$store.getters.canBuy
		},
		cartHasFreeItem() {
			return this.$store.getters['user/currCartHasFreeItem'](this.qrCodeHash)
		},
	},
	methods: {
		...mapMutations('user', ['REMOVE_CART_ITEM', 'UPDATE_CART_ITEM']),
		hasConflict(item) {
			return !!item.conflicts?.length
		},
		conflictMessages(item) {
			// 同文字衝突（如多個套餐細項異動）去重後顯示，訊息文字即穩定 key
			return [...new Set((item.conflicts || []).map(formatConflictMessage))]
		},
		canAccept(item) {
			return canAcceptNewPrice(item.conflicts)
		},
		// 商品已下架 / 售完時重新選購無意義，僅能刪除
		canRepick(item) {
			return !(item.conflicts || []).some(
				conflict =>
					conflict.type === CART_CONFLICT_TYPES.ITEM_REMOVED ||
					conflict.type === CART_CONFLICT_TYPES.ITEM_SOLD_OUT,
			)
		},
		// 衝突輪次第一次重新選購才重拉 getMenu：口味 / 套餐等巢狀新資料
		// 只能來自 getMenu；同輪後續品項共用已同步的完整快照。
		async repickConflictItem(item, index) {
			if (this.isRepicking) return
			this.isRepicking = true
			try {
				if (!this.isFullMenuFreshForCartConflicts) {
					this.$store.commit('store/BUMP_FULL_MENU_REQUEST_SEQ')
					const request = captureFullMenuRequest({
						storeState: this.$store.state.store,
						socketState: this.$store.state.socket,
						storeId: this.storeId,
					})
					const result = (
						await this.$api.order.getMenu({
							storeId: this.storeId,
							keyword: '',
						})
					).data
					if (
						request.cartConflictRevision !==
						this.$store.state.store.cartConflictRevision
					) {
						this.$alert('購物車資料已更新，請再試一次', 'danger')
						return
					}
					this.$store.commit('store/SET_FULL_MENU_SNAPSHOT', {
						...request,
						menuList: result.menuList,
						fetchedAt: Date.now(),
					})
				}
				this.handleEditProduct(item, index)
			} catch (err) {
				$devLog('重新選購前刷新菜單失敗', err)
				this.$alert('菜單更新失敗，請稍後再試', 'danger')
				return
			} finally {
				this.isRepicking = false
			}
		},
		// 以最新價格保留該筆並清除衝突標記（僅衝突只有 PRICE_CHANGED 時可用，見 canAcceptNewPrice）
		acceptNewPrice(item, index) {
			const { newPrice } = item.conflicts[0]
			const data = { ...item, itemPrice: newPrice }
			delete data.conflicts

			// 折扣須跟著新價重算，否則 PERCENTAGE 折抵與贈品全額折抵會停在舊價基數
			if (data.freeItem) {
				// 贈品的 discountPrice 恆等於單價（全額折抵，見 goFreeItemDetail）
				data.discountPrice = newPrice
			} else if (this.automaticDiscountListReady) {
				// 折扣清單尚未就緒時不動折扣欄位，避免把既有折扣誤清成 null
				Object.assign(
					data,
					resolveItemAutomaticDiscount(data, this.itemAutomaticDiscountList),
				)
			}

			this.UPDATE_CART_ITEM({ index, data })
			this.$alert('已更新為新價格', 'success')
		},
		// 商品下架或售完時無法透過編輯解除衝突，只保留刪除入口。
		canEditItem(item) {
			return this.canRepick(item)
		},
		async handleEditClick(item, index) {
			if (this.isRepicking) return
			if (!this.canEditItem(item)) {
				this.$alert('商品已下架或售完，請直接刪除此項', 'danger')
				return
			}
			if (this.hasConflict(item)) {
				await this.repickConflictItem(item, index)
				return
			}
			this.handleEditProduct(item, index)
		},
		handleEditProduct(item, index) {
			if (!this.canEditItem(item)) {
				this.$alert('商品已下架或售完，請直接刪除此項', 'danger')
				return
			}
			this.$store.commit('detail/setCacheCart', { data: item, index })
			this.editFun != null
				? this.editFun(item.itemId)
				: this.$router.push('Detail/' + item.itemId + '?edit=true')
		},
		touchStart(e) {
			this.startX = Math.floor(e.touches[0].pageX)
		},
		touchend(e, item, index) {
			this.endX = Math.floor(e.changedTouches[0].pageX)
			if (this.endX - this.startX < -30) {
				this.cartItem('moveLeft', item, index)
			} else {
				this.cartItem('moveRight', item, index)
			}
		},
		onShowDeleteCartItemDialog(item, index) {
			this.cacheDeleteCartItemState.index = index
			this.cacheDeleteCartItemState.name = item.itemName
			this.isDeleteCartItemDialog = true
		},
		onHideDeleteCartItemDialog() {
			this.isDeleteCartItemDialog = false
		},
		deleteCartItem() {
			const {
				cacheDeleteCartItemState: { index },
			} = this
			const cartInfos = [...document.getElementsByClassName('cart-info')]
			const deleteBtn = [...document.getElementsByClassName('cart-delete')]
			this.cart.map((row, i) => {
				if (i === index) {
					this.REMOVE_CART_ITEM({ index: i })
				}
			})
			cartInfos.forEach((info, i) => {
				if (i === index) {
					info.style.transition = 'initial'
					info.style.right = '0px'
					deleteBtn.forEach((del, _index) => {
						if (_index === index) {
							del.style.transition = 'initial'
							del.style.right = '-70px'
						}
					})
				}
			})
			this.onHideDeleteCartItemDialog()
			this.$alert('已刪除成功', 'success')
		},
		deleteItem(item, index) {
			this.onShowDeleteCartItemDialog(item, index)
		},
		cartItem(type, item, index, eventCount = 0) {
			let cartInfos = [...document.getElementsByClassName('cart-info')]
			let deleteBtn = [...document.getElementsByClassName('cart-delete')]
			switch (type) {
				case 'moveLeft':
					cartInfos.forEach((info, i) => {
						if (i === index) {
							info.style.transition = 'all 0.5s ease'
							info.style.right = '70px'
							deleteBtn.forEach((del, _index) => {
								if (_index === index) {
									del.style.transition = 'all 0.5s ease'
									del.style.right = '0px'
								}
							})
						}
					})
					break
				case 'moveRight':
					cartInfos.forEach((info, i) => {
						if (i === index) {
							info.style.transition = 'all 0.5s ease'
							info.style.right = '0px'
							deleteBtn.forEach((del, _index) => {
								if (_index === index) {
									del.style.transition = 'all 0.5s ease'
									del.style.right = '-70px'
								}
							})
						}
					})
					break
				case 'minus':
					this.UPDATE_CART_ITEM({
						index: index,
						data: {
							...item,
							count: eventCount,
						},
					})
					if (item.count === 1 && eventCount === 1) {
						cartInfos.forEach((info, _i) => {
							if (_i === index) {
								info.style.transition = 'all 0.5s ease'
								info.style.right = '70px'
								deleteBtn.forEach((del, _index) => {
									if (_index === index) {
										del.style.transition = 'all 0.5s ease'
										del.style.right = '0px'
									}
								})
							}
						})
					}
					break
				case 'plus':
					this.UPDATE_CART_ITEM({
						index: index,
						data: {
							...item,
							count: eventCount,
						},
					})
					break
				default:
					break
			}
		},
		goFreeItemDetail() {
			// 找出折扣item
			const automaticDiscountItem =
				this.useOrderAutomaticDiscount.automaticDiscountItemList[0]

			const item =
				this.menus
					.find(
						category =>
							category.categoryId === automaticDiscountItem.categoryId,
					)
					?.itemList.find(
						item => item.itemId === automaticDiscountItem.itemId,
					) || null

			if (!item) {
				this.$alert(item.itemName + ' 找不到贈品', 'danger')
				return
			}

			if (!item.availableForSale) {
				this.$alert(item.itemName + ' 已售完', 'danger')
				return
			}
			const hasOptions = item.optionList && item.optionList.length > 0
			const hasUpgrades = item.upgradeList && item.upgradeList.length > 0
			const hasCombos = item.comboList && item.comboList.length > 0
			if ((hasOptions || hasUpgrades || hasCombos) && this.canBuy) {
				if (this.isKiosk) {
					this.detailId = item.itemId
					this.isEditDetail = false
					this.isDetailDialog = true
					return
				}
				// 前往item頁面
				this.$router.push(`Detail/` + item.itemId)
			} else if (this.canBuy) {
				const store = {
					count: 1,
					itemId: item.itemId,
					itemName: item.itemName,
					itemOptionDetailList: [],
					itemPrice: item.price,
					itemRemark: item.remark,
					comboList: item.comboList || [],
					automaticDiscountId: this.useOrderAutomaticDiscount.id,
					automaticDiscountName: this.useOrderAutomaticDiscount.name,
					automaticDiscountItemId:
						automaticDiscountItem.automaticDiscountItemId,
					discountPrice: item.price, // free_item會減去全額
					freeItem: true,
				}
				this.$alert(`${item.itemName} 已加入購物車`, 'success')
				this.$store.commit('user/LOGIN_AMOUNT_CART', {
					cartItems: [store],
					isResetCart: false,
				})
			}
		},
	},
}
</script>
<style scoped lang="sass">

// 衝突提示配色（專案無共用色彩變數，先在檔內收斂，避免紅色散落各處）
$conflict-red: #D52828
$conflict-bg: #FFF5F5
$repick-orange: #EDB477
$text-dark: #432D2C
$brand-main: #294B70 // 同 Button.vue 的 .btn-main1

.cart
	&-grid
		font-size: 16px

	&-items
		position: relative
		// 平時就以透明邊框佔位，衝突時只換顏色 —— 否則加上四邊 border 會讓該列高度與內容位移
		border: 1px solid transparent
		border-bottom: 1px solid #D8D8D899
		overflow: hidden

		&:last-child
			border-bottom: 1px solid #D8D8D899

		&--conflict
			border-color: $conflict-red
			border-radius: 4px
			margin: 8px 0
			padding: 8px

			// 需壓過 .cart-items:last-child 的灰色底線，否則最後一筆紅框缺底邊
			&:last-child
				border-bottom-color: $conflict-red

	&-conflict
		// .cart-info 是 flex 容器，flex item 的 width: auto 會收縮成內容寬（訊息越長框越寬）；
		// 佔滿整行才能讓右緣穩定對齊。不縮排 —— 這個警告描述的是整筆餐點，
		// 不是某一行口味的附註（縮排會讓它看起來從屬於上方的 OrderList）。
		width: 100%
		margin-top: 8px
		padding: 8px 10px
		border-left: 3px solid $conflict-red
		border-radius: 4px
		background: $conflict-bg
		font-size: 14px
		color: $conflict-red

		&-reason
			margin: 5px 0 0

		&-icon
			margin-right: 4px

		&-actions
			display: flex
			// 按鈕在窄螢幕需換行，否則被 .cart-items 的 overflow: hidden 裁掉
			flex-wrap: wrap
			gap: 8px
			margin-top: 8px

	&-conflict-btn
		min-height: 40px
		padding: 8px 16px
		border: none
		border-radius: 4px
		font-size: 14px

		&:disabled
			opacity: .5

		// 接受新價是正向確認動作，用主色而非紅色（紅色語意為危險）
		&--accept
			background: $brand-main
			color: #fff

		// 次要動作用暖色與錯誤紅區隔，深色字確保淺底對比 —— 紅框本身已是紅底紅字紅邊，
		// 按鈕再用紅會讓整區只剩一種顏色，看不出哪裡可以按
		&--repick
			background: $repick-orange
			color: $text-dark

	&-btn
		width: 15px

		img
			width: 100%
			height: 100%

		// 不可編輯或正在刷新菜單時淡化，保留位置避免該列版面位移。
		&--disabled
			opacity: .3

	&-info
		width: 100%
		padding: 15px
		display: flex
		flex-wrap: wrap
		align-items: center
		box-sizing: border-box
		float: left
		position: relative
		right: 0
		transition: all 0.5s ease

	&-name
		flex: 1
		padding: 0 10px

	&-price
		position: relative
		top: -3px
		flex-basis: 20%
		text-align: right
		color: #432D2C

	&-order-list
		margin-left: 25px

	&-discount
		color: #f37201
		width: 100%
		margin-left: 25px
		font-size: 14px

		&-content
			margin-left: 12px
			margin-top: 7px
			display: flex
			justify-content: space-between

	&-order-discount

		position: relative
		padding: 20px 15px 0 15px

		&-use
			color: #f37201
			display: flex
			justify-content: space-between
			align-items: center

			&-text
				display: flex
				align-items: center

				.go-free-item-detail-button
					width: 26px
					height: 26px
					vertical-align: middle

		&-next
			margin-top: 15px
			color: #D9001B

	// 折抵吃滿整筆時的說明，接在總計上方，避免客人以為畫面算錯
	&-count-capped
		padding: 15px 15px 0
		color: #f37201
		font-size: 14px
		text-align: right

	&-count
		position: relative
		display: flex
		justify-content: space-between
		margin-bottom: 25px
		font-weight: bold
		font-size: 18px

		span
			padding: 20px 15px

		span:nth-last-child(1)
			color: #432D2C

	&-delete
		width: 70px
		color: #707070
		background-color: #eeeeee
		padding: 5px 10px
		font-size: 16px
		text-align: center
		transition: all 0.5s ease
		position: absolute
		top: 0
		right: -70px
		float: left
		height: 100%
		box-sizing: border-box

		.trashcan
			width: 16px
			position: relative
			left: 50%
			top: 50%
			transform: translate(-50%, -50%)

			&:after
				content: '刪除'
				display: block
				position: relative
				width: 30px
				left: -7px
				padding-top: 3px
				font-size: 14px

		p
			position: relative
			left: 50%
			top: 50%
			transform: translate(-50%, -50%)
</style>
